function text(value) {
  return String(value || '').trim();
}

function formatOfferDate(value, locale = 'en-US', timeZone = '') {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const options = { month: 'short', day: 'numeric', year: 'numeric' };
  if (timeZone) options.timeZone = timeZone;
  try { return parsed.toLocaleDateString(locale, options); }
  catch { return parsed.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }); }
}

function formatOfferTime(value, locale = 'en-US') {
  const match = String(value || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  const date = new Date(2000, 0, 1, Number(match[1]), Number(match[2]));
  return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

function offerRecurrenceLabel(rule = {}, locale = 'en-US') {
  if (!rule || typeof rule !== 'object' || text(rule.frequency).toUpperCase() !== 'WEEKLY') return '';
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = (Array.isArray(rule.daysOfWeek) ? rule.daysOfWeek : []).map((day) => dayNames[Number(day)]).filter(Boolean).join(', ');
  const start = formatOfferTime(rule.startTime, locale);
  const end = formatOfferTime(rule.endTime, locale);
  const time = start && end ? `${start}–${end}` : start || end;
  return [`Every ${days || 'week'}`, time].filter(Boolean).join(' · ');
}

function offerValueBadge(offer = {}) {
  const type = text(offer.offerType).toUpperCase();
  const title = text(offer.title).toUpperCase();
  if (type === 'PERCENT_OFF') return title.match(/\b\d{1,3}%\s*OFF\b/)?.[0] || 'PERCENT OFF';
  if (type === 'DOLLAR_OFF') return title.match(/\$[\d,.]+\s*OFF\b/)?.[0] || 'SAVINGS';
  if (type === 'BUY_ONE_GET_ONE') return 'BUY 1 GET 1';
  if (type === 'FREE_ITEM') return 'FREE ITEM';
  if (type === 'FREE_ESTIMATE') return 'FREE ESTIMATE';
  if (type === 'FREE_CONSULTATION') return 'FREE CONSULTATION';
  if (type === 'PACKAGE') return 'PACKAGE DEAL';
  if (type === 'EVENT_PROMOTION') return 'EVENT SPECIAL';
  if (type === 'GIVEAWAY') return 'GIVEAWAY';
  if (type === 'COUPON') return 'COUPON';
  if (type === 'LIMITED_TIME_SPECIAL') return 'SPECIAL OFFER';
  return 'LOCAL OFFER';
}

function offerCustomerAction(offer = {}) {
  const captureMode = text(offer.captureMode).toUpperCase();
  const method = text(offer.redemptionMethod).toUpperCase();
  const destination = text(offer.destinationUrl || offer.redemptionUrl);
  const phone = text(offer.phoneNumber || offer.listing?.phone || offer.listing?.phoneNumber);

  if (captureMode && captureMode !== 'NONE') return {
    label: 'Send Me This Offer',
    instructions: 'Send the Offer to your email or phone so it is ready when you need it.',
    kind: 'capture',
    href: '',
  };
  if (method === 'CALL_BUSINESS' && phone) return {
    label: 'Call to Claim',
    instructions: 'Call the business to ask for this Offer.',
    kind: 'phone',
    href: `tel:${phone}`,
  };
  if (destination) return {
    label: /book|appoint|reserv/i.test(destination) ? 'Book This Offer' : 'Claim on Website',
    instructions: /book|appoint|reserv/i.test(destination)
      ? 'Book through the business website and mention this Offer.'
      : "Use this Offer through the business's website.",
    kind: 'external',
    href: destination,
  };
  return {
    label: 'Get This Offer',
    instructions: 'Tap “Get This Offer” and show it to the business when you are ready to use it.',
    kind: 'immediate',
    href: '',
  };
}

function buildOfferDisplayModel(offer = {}, { tenant = {}, locale = 'en-US' } = {}) {
  const listing = offer.listing || {};
  const timeZone = text(offer.timezone || offer.recurrenceRule?.timezone || tenant.timezone);
  const startDate = formatOfferDate(offer.validFrom || offer.startAt, locale, timeZone);
  const endDate = formatOfferDate(offer.validThrough || offer.endAt, locale, timeZone);
  const status = text(offer.computedStatus || offer.status).toUpperCase();
  const expirationValue = offer.validThrough || offer.endAt;
  const expirationDate = expirationValue ? new Date(expirationValue) : null;
  const expired = status === 'EXPIRED' || Boolean(expirationDate && !Number.isNaN(expirationDate.getTime()) && expirationDate < new Date());
  const address = text(listing.address) || [listing.addressLine1, listing.addressLine2, listing.city, listing.state, listing.postalCode || listing.zipCode]
    .map(text).filter(Boolean).join(', ');
  const terms = Array.from(new Set([
    ...(Array.isArray(offer.finePrintPresetTerms) ? offer.finePrintPresetTerms.map((term) => text(term.body || term.description)) : []),
    text(offer.terms),
  ].filter(Boolean)));
  const action = offerCustomerAction(offer);
  const recurrenceLabel = offerRecurrenceLabel(offer.recurrenceRule, locale);
  const availabilityLabel = expired
    ? 'This Offer has ended'
    : recurrenceLabel || (startDate && endDate ? `Available ${startDate}–${endDate}` : endDate ? `Available through ${endDate}` : startDate ? `Available starting ${startDate}` : 'Available now');

  return {
    id: offer.id || null,
    slug: text(offer.slug),
    headline: text(offer.title) || 'Local Offer',
    shortDescription: text(offer.shortDescription),
    about: text(offer.description || offer.fullDescription),
    contentJson: offer.contentJson || null,
    businessName: text(listing.name || listing.displayName) || 'Local Business',
    businessLocation: [text(listing.city), text(listing.state)].filter(Boolean).join(', '),
    address,
    phone: text(listing.phone || listing.phoneNumber),
    website: text(listing.websiteUrl || listing.website),
    image: text(offer.imageUrl || offer.coverImage?.publicUrl || offer.image)
      || text(listing.coverImageUrl || listing.imageUrl || listing.image)
      || text(listing.logoUrl)
      || text(tenant.defaultOfferImageUrl || tenant.offerDefaultImageUrl),
    imageAlt: text(offer.imageAlt) || `${text(offer.title) || 'Offer'} from ${text(listing.name) || 'a local business'}`,
    badge: offerValueBadge(offer),
    availabilityLabel,
    recurrenceLabel,
    startDate,
    endDate,
    expired,
    terms,
    action,
    tenantName: text(tenant.name || offer.tenantName) || 'My Town Spotlight',
  };
}

export { buildOfferDisplayModel, formatOfferDate, offerCustomerAction, offerRecurrenceLabel, offerValueBadge };
export default { buildOfferDisplayModel, formatOfferDate, offerCustomerAction, offerRecurrenceLabel, offerValueBadge };
